import Colors from '@dynatrace/strato-design-tokens/colors';
import Typography from '@dynatrace/strato-design-tokens/typography';

import { SparklineXAxisInternalProps } from './types/sparkline-x-axis-props.js';
import type { SparklineYAxisProps } from './types/sparkline-y-axis-props.js';

export const defaultXAxisConfig: Required<SparklineXAxisInternalProps> = {
  min: 'data-min',
  max: 'data-max',
};

export const defaultYAxisConfig: Required<
  Omit<SparklineYAxisProps, 'formatter'>
> = {
  min: 'data-min',
  max: 'data-max',
  scale: 'linear',
};

export const DEFAULT_SCALE_FALLBACK_COLOR = Colors.Text.Neutral.Default;

export const DEFAULT_SHOW_CONTEXT_VALUES = false;

export const TIMESTAMP_BAR_WIDTH = 5;
export const TIMESTAMP_BAR_X_DEVIATION = 2;

export const CONTEXT_VALUE_STROKE_DASHARRAY = '2,2';
export const CONTEXT_VALUE_STROKE_LINECAP = 'round';
export const CONTEXT_VALUE_STROKE_COLOR =
  Colors.Background.Field.Neutral.DefaultActive;

export const CONTEXT_VALUES_Y_OFFSET = 0;
export const AXIS_HEIGHT = 25;

export const CONTEXT_VALUE_LINE_HEIGHT = 12;
export const CONTEXT_VALUE_LARGE_LINE_HEIGHT = 16;
export const CONTEXT_VALUE_TEXT_OFFSET_MAX = 4;
export const CONTEXT_VALUE_TEXT_OFFSET_MIN_SMALL = 8;
export const CONTEXT_VALUE_TEXT_OFFSET_MIN_LARGE = 12;
export const CONTEXT_VALUE_LABELS_GAP = 8;
// Minimum distance between raw y-positions so text labels don't overlap.
// Derived from the text-box geometry per font size:
//   small: textOffsetMinSmall(8) + lineHeight(12) + gap(8) - textOffsetMax(4) = 24
//   large: textOffsetMinLarge(12) + lineHeightLarge(16) + gap(8) - textOffsetMax(4) = 32
export const CONTEXT_VALUE_MIN_LABELS_DISTANCE_SMALL =
  CONTEXT_VALUE_TEXT_OFFSET_MIN_SMALL +
  CONTEXT_VALUE_LINE_HEIGHT +
  CONTEXT_VALUE_LABELS_GAP -
  CONTEXT_VALUE_TEXT_OFFSET_MAX;
export const CONTEXT_VALUE_MIN_LABELS_DISTANCE_LARGE =
  CONTEXT_VALUE_TEXT_OFFSET_MIN_LARGE +
  CONTEXT_VALUE_LARGE_LINE_HEIGHT +
  CONTEXT_VALUE_LABELS_GAP -
  CONTEXT_VALUE_TEXT_OFFSET_MAX;

export const CONTEXT_VALUES_FONT = Typography.Text.Small.Emphasized;
export const CONTEXT_VALUE_MAX_WIDTH = 300;

export const SPARKLINE_ARIA_LABEL = 'Sparkline';

export const CONTEXT_VALUE_INDICATOR_X_OFFSET = 8;

// Horizontal space always reserved for the negative sign and its CSS marginLeft,
// so that the chart width is stable regardless of whether values are negative.
// sign-div width + Spacings.Size4 (4 px) CSS marginLeft.
export const CONTEXT_VALUE_SIGN_COLUMN_SMALL = 8; // 4 px sign  + 4 px margin (height ≤ 24)
export const CONTEXT_VALUE_SIGN_COLUMN_LARGE = 10; // 6 px sign + 4 px margin (height > 24)

export const NEGATIVE_SIGN = '-';

/** 1 px visual nudge + 4 px CSS marginLeft on the sign element. */
export const CONTEXT_VALUE_SIGN_NUDGE_RIGHT = 5;

/** Padding added after the measured label + sign column. */
export const CONTEXT_VALUE_TRAILING_PADDING = 3;

/** Height threshold (px) for sign-column sizing: <= uses SMALL, > uses LARGE. */
export const CONTEXT_VALUE_SIGN_COLUMN_HEIGHT_THRESHOLD = 24;

export const FONT_SIZE_BREAKING_POINT = 32;
